/**
 * CSV export modal — right column (sort, cross-source option, templates).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC, RefObject } from 'react';

import {
  CsvExportServerSortSection,
  type ServerCsvExportSortConfig,
} from '../../server-sort';
import type { CsvExportTemplate } from '../../useCsvExportTemplates';
import { CsvExportCrossSourceExcludeControl } from '../cross-source';
import { CsvExportDownloadNameField } from '../download-name';
import { CsvExportTemplatesSection } from '../CsvExportTemplatesSection';

export interface CsvExportModalAsideProps {
  hasExportSortOptions: boolean;
  serverCsvSortConfig: ServerCsvExportSortConfig | undefined;
  isServerCsvExport: boolean;
  showCrossSourceExclude: boolean;
  exportColumnKeys: string[];
  excludeCrossSourceNotedRows: boolean;
  setExcludeCrossSourceNotedRows: (v: boolean) => void;
  serverExporting: boolean;
  exportSortField: string;
  exportSortDir: 'asc' | 'desc';
  setExportSortField: (v: string) => void;
  setExportSortDir: (v: 'asc' | 'desc') => void;
  templates: CsvExportTemplate[];
  editingTemplateId: string | null;
  canManageTemplates: boolean;
  templatesBusy: boolean;
  templateName: string;
  templateColor: string;
  saveAsDefault: boolean;
  setTemplateName: (v: string) => void;
  setTemplateColor: (v: string) => void;
  setSaveAsDefault: (v: boolean) => void;
  applyTemplate: (t: CsvExportTemplate) => void;
  deleteTemplate: (t: CsvExportTemplate) => boolean;
  handleSaveTemplate: () => void;
  handleUpdateTemplate: () => void;
  exportDownloadBaseName: string;
  onExportDownloadBaseNameChange: (v: string) => void;
  downloadFilenameInputRef?: RefObject<HTMLInputElement>;
}

export const CsvExportModalAside: FC<CsvExportModalAsideProps> = (p) => (
  <aside className="flex min-h-0 w-full shrink-0 flex-col gap-4 overflow-y-auto border-t border-border/40 px-5 py-4 lg:w-[min(100%,360px)] lg:border-t-0 xl:w-[380px]">
    <CsvExportDownloadNameField
      ref={p.downloadFilenameInputRef}
      value={p.exportDownloadBaseName}
      disabled={p.serverExporting}
      onChange={p.onExportDownloadBaseNameChange}
    />
    {p.hasExportSortOptions && p.serverCsvSortConfig ? (
      <CsvExportServerSortSection
        config={p.serverCsvSortConfig}
        field={p.exportSortField}
        dir={p.exportSortDir}
        disabled={p.serverExporting}
        onFieldChange={p.setExportSortField}
        onDirChange={p.setExportSortDir}
        defaultOptionLabel={
          p.isServerCsvExport
            ? 'Default (streamed — fastest for large exports)'
            : 'Default (same as table)'
        }
        sortPerformanceHint={
          p.isServerCsvExport
            ? 'Choosing a sort column buffers the full dataset before download.'
            : undefined
        }
      />
    ) : null}
    {p.showCrossSourceExclude && p.exportColumnKeys.includes('crossSourceFilterNote') ? (
      <CsvExportCrossSourceExcludeControl
        checked={p.excludeCrossSourceNotedRows}
        disabled={p.serverExporting}
        onCheckedChange={p.setExcludeCrossSourceNotedRows}
      />
    ) : null}
    <CsvExportTemplatesSection
      templates={p.templates}
      exportColumnKeys={p.exportColumnKeys}
      editingTemplateId={p.editingTemplateId}
      canManageTemplates={p.canManageTemplates}
      templatesBusy={p.templatesBusy}
      disabled={p.serverExporting}
      templateName={p.templateName}
      templateColor={p.templateColor}
      saveAsDefault={p.saveAsDefault}
      onTemplateNameChange={p.setTemplateName}
      onTemplateColorChange={p.setTemplateColor}
      onSaveAsDefaultChange={p.setSaveAsDefault}
      onApplyTemplate={p.applyTemplate}
      onDeleteTemplate={p.deleteTemplate}
      onSaveTemplate={p.handleSaveTemplate}
      onUpdateTemplate={p.handleUpdateTemplate}
    />
  </aside>
);
